<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute, useRouter, onBeforeRouteUpdate } from 'vue-router'
import { useScreen } from '@/composables/useScreen'
import { useDebounceFn, watchDebounced } from '@vueuse/core'
import { useAsyncData } from '#app'
import { useHead, useRuntimeConfig } from '#imports'

const route = useRoute()
const router = useRouter()
const axios = useAxios()
const { isMobileView } = useScreen()
const { t } = useI18n()

const sortingOptions = ref(['ourRecommended', 'lowestPrice', 'rating', 'nearestTime', 'amountOfFeedback'])
const arrayFilterKeys = ['specialisation', 'city', 'language']

const defaultFilterState = JSON.stringify({
  page: 1,
  language: [],
  city: [],
  specialisation: [],
  sorting: sortingOptions.value[0],
  query: null,
  display: 'all'
})

function getDefaultFilters() {
  return JSON.parse(defaultFilterState)
}

const filters = ref(getDefaultFilters())
const filtersRef = ref({})
const sliderModel = ref([0, 0])
const isInitialLaunch = ref(true)

const entityTotal = computed(() => pageData.value.entityTotal)

function parseFiltersFromUrl() {
  for (const key in route.query) {
    if (arrayFilterKeys.includes(key)) {
      const items = Array.isArray(route.query[key]) ? route.query[key] : [route.query[key]]
      items.forEach(item => updateFilters(key, item))
    } else {
      if (key === 'page') {
        const pageNum = +route.query[key]
        if (pageNum > 0) {
          filters.value.page = pageNum
        }
        continue
      }
      if (key.includes('hourlyConsultationPrice')) {
        if (key.includes('From')) {
          sliderModel.value[0] = +route.query[key]
          filters.value.hourlyConsultationPriceFrom = +route.query[key]
        }
        if (key.includes('Till')) {
          sliderModel.value[1] = +route.query[key]
          filters.value.hourlyConsultationPriceTill = +route.query[key]
        }
      } else {
        filters.value[key] = route.query[key]
      }
    }
  }
}

function updateFilters(key, selectedItem) {
  if (arrayFilterKeys.includes(key)) {
    const arr = filters.value[key] ?? []
    if (!arr.includes(selectedItem)) {
      arr.push(selectedItem)
    }
    filters.value[key] = arr
  } else {
    filters.value[key] = selectedItem
  }
}

parseFiltersFromUrl()

const cacheKey = computed(() => {
  const specialty = route.params.specialty as string
  const display   = filters.value.display
  const page      = String(filters.value.page)

  return ['lawyers-page', specialty, display, page].join(':')
})
const { data: pageData, status, refresh: refreshData } = useAsyncData(unref(cacheKey),
  async () => {
    try {
      const promiseFilters = axios.get('/lawyers/available-filters', {
        params: { specialty: route.params.specialty }
      })

      const endpointMap = {
        all: 'lawyers-and-firms',
        lawyers: 'lawyers',
        firms: 'firms'
      }
      const endpoint = endpointMap[filters.value.display] || 'lawyers-and-firms'

      const promiseEntities = axios.get(endpoint, {
        params: {
          specialty: route.params.specialty,
          ...(() => {
            const clean = { ...filters.value }
            delete clean.display
            return clean
          })()
        }
      })

      const [filtersRes, entityRes] = await Promise.all([
        promiseFilters,
        promiseEntities
      ])

      const total = parseInt(entityRes.headers['x-total-count'] || '0', 10)

      if (isInitialLaunch.value) {
        setPriceFilter(filtersRes.data)
        parseFiltersFromUrl()
      }

      return {
        availableFilters: filtersRes.data,
        entityArray: entityRes.data,
        entityTotal: total
      }
    } catch (err) {
      console.error('SSR fetch failed:', err)
      return {
        availableFilters: {},
        entityArray: [],
        entityTotal: 0
      }
    }
  },
  {
    immediate: true,
    default: () => ({
      availableFilters: {},
      entityArray: []
    })
  }
)

const isLoading = computed(() => status.value === 'pending')
const availableFilters = computed(() => pageData.value?.availableFilters || {})
const entityArray = computed(() => pageData.value?.entityArray || [])
const filterStatus = computed(() => isLoading.value ? 'pending' : 'success')
const entityStatus = computed(() => isLoading.value ? 'pending' : 'success')
const isFiltersLoading = computed(() => filterStatus.value === 'pending')
const isLawyersLoading = computed(() => entityStatus.value === 'pending')

const isDataLoading = computed(() =>
  (isInitialLaunch.value && (isFiltersLoading.value || isLawyersLoading.value)) 
   || isLawyersLoading.value
)

watchDebounced(
  () => isLoading.value,
  (val) => {
    if (!val) isInitialLaunch.value = false
  },
  { debounce: 0 }
)

const dropdownSpecialisations = computed(() =>
  availableFilters.value?.specialisations?.map(spec => ({
    id: spec.slug, label: spec.name
  })) ?? []
)

const dropdownLocation = computed(() =>
  availableFilters.value?.city?.map(city => ({
    id: city.name,
    label: city.name,
    disabled: city.disabled
  })) ?? []
)

const dropdownLanguages = computed(() =>
  availableFilters.value?.language?.map(language => ({
    id: language.name,
    label: language.name
  })) ?? []
)

function onSingleFilterValueChange(key, selectedItem) {
  if (key === 'page') {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  } else {
    resetFilters('page', false)
  }

  if (!isMobileView.value && filtersRef.value[key]?.isOpen) {
    filtersRef.value[key]?.closeDropdown()
  }

  if (!selectedItem) return
  if (key === 'hourlyRates') {
    filters.value.hourlyConsultationPriceFrom = selectedItem.from
    filters.value.hourlyConsultationPriceTill = selectedItem.till
  } else if (arrayFilterKeys.includes(key)) {
    const arr = filters.value[key] || []
    const idx = arr.indexOf(selectedItem)
    if (idx === -1) arr.push(selectedItem)
    else arr.splice(idx, 1)
    filters.value[key] = arr
  } else {
    filters.value[key] = selectedItem
  }

  updateQuery()
  loadData()
}

function loadData() {
  refreshData()
}

function resetFilters(key, load = true) {
  if (!isMobileView.value && filtersRef.value[key]?.isOpen) {
    filtersRef.value[key]?.closeDropdown()
  }

  if (key && key !== 'price') {
    filters.value[key] = getDefaultFilters()[key]
  } else if (!key) {
    Object.assign(filters.value, getDefaultFilters())
  } else if (key === 'price') {
    setPriceFilter(availableFilters.value)
  }

  updateQuery()
  if (load) {
    loadData()
  }
}

function updateQuery() {
  const query = Object.entries(filters.value).reduce((acc, [key, value]) => {
    if (JSON.stringify(value) === JSON.stringify(getDefaultFilters()[key])) return acc
    return { ...acc, [key]: value }
  }, {})

  router.push({ name: route.name, params: route.params, query })
}

function setPriceFilter(data) {
  if (typeof data.hourlyConsultationPriceFrom !== 'undefined') {
    filters.value.hourlyConsultationPriceFrom = data.hourlyConsultationPriceFrom
    sliderModel.value[0] = data.hourlyConsultationPriceFrom
  }
  if (typeof data.hourlyConsultationPriceTill !== 'undefined') {
    filters.value.hourlyConsultationPriceTill = data.hourlyConsultationPriceTill
    sliderModel.value[1] = data.hourlyConsultationPriceTill
  }
}

const isPriceFilterSelected = computed(() => {
  const from = filters.value.hourlyConsultationPriceFrom
  const till = filters.value.hourlyConsultationPriceTill
  const defFrom = availableFilters.value.hourlyConsultationPriceFrom
  const defTill = availableFilters.value.hourlyConsultationPriceTill
  return (
    (from || till) &&
    (from !== defFrom || till !== defTill)
  )
})

const onSearchInput = useDebounceFn(() => {
  updateQuery()
  loadData()
}, 500)

onBeforeRouteUpdate((to) => {
  if (Object.keys(to.query).length === 0) {
    resetFilters(null)
  }
})

const mobileSearchModalRef = ref(null)

// ---------------- SEO improvements ----------------
const config = useRuntimeConfig()

// Page <title> and <meta> description. 
const pageTitle = computed(() => {
  const specialty = route.params.specialty ? route.params.specialty : ''
  const baseTitle = specialty ? `${t('find_lawyer')}: ${specialty}` : t('find_lawyer')
  return `${baseTitle} - GeriTeisininkai`
})

// Basic meta description.
const metaDescription = computed(() => {
  return t('register_with_the_desired_lawyer_description')
})

// Canonical link
const canonicalLink = computed(() => {
  const baseUrl = config.public.baseURL.includes('localhost')
    ? 'https://geriteisininkai.lt'
    : config.public.baseURL
  return `${baseUrl}${route.fullPath}`
})

// JSON-LD structured data. Using an ItemList for search results
const structuredData = computed(() => {
  if (!entityArray.value.length) return {}

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "All Lawyers & Firms",
    "itemListElement": entityArray.value.map((entity, index) => {
      const slug = entity.slug
      const type = entity.entity_type // "lawyer" or "firm"
      // Decide how you build each entity’s URL
      const url = type === 'lawyer'
        ? `${config.public.baseURL}/lawyers/${slug}`
        : `${config.public.baseURL}/firms/${slug}`

      return {
        "@type": "ListItem",
        position: index + 1,
        url,
        name: entity.name
      }
    })
  }
})

useHead({
  title: pageTitle.value,
  meta: [
    {
      name: 'description',
      content: metaDescription.value
    },
    {
      property: 'og:title',
      content: pageTitle.value
    },
    {
      property: 'og:description',
      content: metaDescription.value
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: canonicalLink.value
    }
  ],
  script: [
    {
      type: 'application/ld+json',
      children: computed(() => JSON.stringify(structuredData.value))
    }
  ]
})
</script>

<template>
  <section :key="route.url" class="flex flex-col flex-grow gap-6 font-branding">
    <div class="grid gap-4 py-8 font-redhat">
      <h1 class="text-lightBlue text-4xl lg:text-6xl mb-4 text-center font-semibold leading-[40px] lg:leading-[66px]">
        {{ $t('find_lawyer') }}
      </h1>
      <div class="max-w-[326px] lg:max-w-[560px] mx-auto flex justify-center items-center">
        <p class="text-lightBlack text-center text-md leading-6">
          {{ $t('register_with_the_desired_lawyer_description') }}
        </p>
      </div>
    </div>

    <div class="flex-grow flex flex-col gap-4">
      <div class="container">
        <ClientOnly>
          <GTRadioButton
            v-model="filters.display"
            :disabled="isDataLoading"
            :options="[
              { id: 'all', label: $t('all') },
              { id: 'lawyers', label: $t('lawyers') },
              { id: 'firms', label: $t('firms') }
            ]"
            class="*:w-[90px] *:lg:w-[169px]"
            @change="onSingleFilterValueChange('page', 1)"
          />
        </ClientOnly>
      </div>

      <div class="bg-gray-100 w-full relative">
        <div class="container w-full pb-0.5 pt-2 lg:py-6">
          <div class="absolute top-0 left-0 h-full bg-gradient-to-r from-gray-100 from-[85%] to-transparent pl-6 pr-3 flex lg:hidden items-center z-[30]">
            <button class="!p-2 rounded-full bg-white" @click="mobileSearchModalRef.open()">
              <IconSearch class="size-6 text-darkBlue" />
            </button>
          </div>
          <div class="pl-14 md:pb-1 lg:pb-0 lg:pl-0 w-full flex items-center gap-2 overflow-x-scroll gutter-stable scrollbar scrollbar--transparent md:overflow-visible">
            <ClientOnly>
              <GTDropdown
                :ref="(el) => filtersRef.specialisation = el"
                :title="$t('specialisations')"
                :isOptionsLoading="isFiltersLoading"
                :isSelected="Boolean(filters.specialisation.length)"
                :labelWrapperClass="('p-2 lg:pl-4 lg:py-2 lg:!pr-6 border lg:border-0 lg:border-r rounded-[10px] lg:rounded-none ' + (Boolean(filters.specialisation.length) ? 'border-lightBlue lg:border-gray-200' : 'border-gray-200'))"
              >
                <template #default="{ isButtons }">
                  <GTDropdownOptions
                    v-model="filters.specialisation"
                    :class="{ 'pb-2': isButtons }"
                    :data="dropdownSpecialisations"
                    :multiple="true"
                    @change="selectedItem => onSingleFilterValueChange('specialisation', selectedItem.id)"
                  />
                </template>

                <template #buttons="{ isSelected, closeDropdown }">
                  <FilterButtons
                    :isSelected="isSelected"
                    @action="closeDropdown"
                    @reset="() => {
                      resetFilters('specialisation')
                      closeDropdown()
                    }"
                  />
                </template>
              </GTDropdown>
            
              <GTDropdown
                :ref="(el) => filtersRef.city = el"
                :title="$t('location')"
                :isOptionsLoading="isFiltersLoading"
                :isSelected="Boolean(filters.city.length)"
                :labelWrapperClass="('p-2 lg:pl-4 lg:py-2 lg:!pr-6 border lg:border-0 lg:border-r rounded-[10px] lg:rounded-none ' + (Boolean(filters.city.length) ? 'border-lightBlue lg:border-gray-200' : 'border-gray-200'))"
              >
                <template #default="{ isButtons }">
                  <GTDropdownOptions
                    v-model="filters.city"
                    class="min-w-[210px]"
                    :class="{ 'pb-2': isButtons }"
                    :data="dropdownLocation.filter(x => !x.disabled)"
                    :multiple="true"
                    :checkbox="true"
                    @change="selectedItem => onSingleFilterValueChange('city', selectedItem.id)"
                  />
                </template>

                <template #buttons="{ isSelected, closeDropdown }">
                  <FilterButtons
                    :isSelected="isSelected"
                    @action="closeDropdown"
                    @reset="() => {
                      resetFilters('city')
                      closeDropdown()
                    }"
                  />
                </template>
              </GTDropdown>

              <GTDropdown
                :ref="(el) => filtersRef.language = el"
                :title="$t('languages')"
                :isOptionsLoading="isFiltersLoading"
                :isSelected="Boolean(filters.language.length)"
                :labelWrapperClass="('p-2 lg:pl-4 lg:py-2 lg:!pr-6 border lg:border-0 lg:border-r rounded-[10px] lg:rounded-none ' + (Boolean(filters.language.length) ? 'border-lightBlue lg:border-gray-200' : 'border-gray-200'))"
              >
                <template #default="{ isButtons }">
                  <GTDropdownOptions
                    v-model="filters.language" 
                    class="min-w-[210px]"
                    :class="{ 'pb-2': isButtons }"
                    :data="dropdownLanguages"
                    :multiple="true"
                    :checkbox="true"
                    @change="selectedItem => onSingleFilterValueChange('language', selectedItem.id)"
                  />
                </template>

                <template #buttons="{ isSelected, closeDropdown }">
                  <FilterButtons
                    :isSelected="isSelected"
                    @action="closeDropdown"
                    @reset="() => {
                      resetFilters('language')
                      closeDropdown()
                    }"
                  />
                </template>
              </GTDropdown>

              <GTDropdown
                :ref="(el) => filtersRef.price = el"
                :title="$t('price')"
                :isOptionsLoading="isFiltersLoading"
                :isSelected="isPriceFilterSelected"
                :labelWrapperClass="('p-2 lg:px-4 lg:py-2 border lg:border-0 rounded-[10px] lg:rounded-none ' + (isPriceFilterSelected ? 'border-lightBlue lg:border-gray-200' : 'border-gray-200'))"
              >
                <template #default>
                  <div class="p-4 grid gap-6">
                    <div class="hidden md:block w-full text-center font-semibold">
                      <span>{{ $t('hourly_rate') }}</span>
                    </div>

                    <GTRangeSlider
                      v-model="sliderModel"
                      :min="availableFilters.hourlyConsultationPriceFrom"
                      :max="availableFilters.hourlyConsultationPriceTill"
                      @change="(value) => onSingleFilterValueChange('hourlyRates', { from: value[0], till: value[1] })"
                    />
                    
                    <div class="w-full flex justify-between items-center gap-2 *:w-1/2 *:grid *:gap-1">
                      <div>
                        <div class="text-sm">
                          {{ $t('from') }}
                        </div>

                        <div class="relative">
                          <input v-model="filters.hourlyConsultationPriceFrom" class="border border-gray-200 rounded-[10px] py-2.5 pr-4 pl-10 max-w-[138px]" />
                          <div class="absolute top-0 left-4 h-full flex items-center">
                            €
                          </div>
                        </div>
                      </div>

                      <div class="text-center">
                        <div class="h-[1em]"></div>
                        <div class="h-[1px] w-[20px] bg-gray-300 mx-auto"></div>
                      </div>

                      <div>
                        <div class="text-sm">
                          {{ $t('till') }}
                        </div>
                        <div class="relative">
                          <input v-model="filters.hourlyConsultationPriceTill" class="border border-gray-200 rounded-[10px] py-2.5 pr-4 pl-10 max-w-[138px]" />
                          <div class="absolute top-0 left-4 h-full flex items-center">
                            €
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <template #buttons="{ isSelected, closeDropdown }">
                  <FilterButtons
                    :isSelected="isSelected"
                    @action="closeDropdown"
                    @reset="() => {
                      resetFilters('price')
                      closeDropdown()
                    }"
                  />
                </template>
              </GTDropdown>

              <GTDropdown
                :ref="(el) => filtersRef.sorting = el"
                :title="$t('sorting_by')"
                :isSelected="filters.sorting !== sortingOptions[0]"
                :labelWrapperClass="('p-2 lg:pl-4 lg:py-2 border rounded-[10px] lg:!px-3 lg:!py-2 ' + (filters.sorting !== sortingOptions[0] ? 'border-lightBlue' : 'border-gray-200'))"
                class="lg:ml-auto"
              >
                <template #labelIcon>
                  <IconSwap class="size-[21px]" />
                </template>
                <template #icon>
                  <IconOldschoolArrow class="size-6" />
                </template>

                <template #default="{ isButtons }">
                  <GTDropdownOptions
                    v-model="filters.sorting"
                    class="min-w-[210px]"
                    :class="{ 'pb-2': isButtons }"
                    :data="sortingOptions.map(x => ({ id: x, label: $t(`sort_option_${x}`) }))"
                    :multiple="false"
                    @change="selectedItem => onSingleFilterValueChange('sorting', selectedItem.id)"
                  />
                </template>

                <template #buttons="{ isSelected, closeDropdown }">
                  <FilterButtons
                    :isSelected="isSelected"
                    @action="closeDropdown"
                    @reset="() => {
                      resetFilters('sorting')
                      closeDropdown()
                    }"
                  />
                </template>
              </GTDropdown>
            </ClientOnly>

            <div class="hidden lg:block">
              <SearchBar
                v-model="filters.query"
                :placeholder="$t('search')+'...'"
                @searchClick="onSearchInput"
                @input="onSearchInput"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="container flex-grow flex flex-col gap-6 pt-4 pb-20">
        <div class="flex-grow relative">
          <ClientOnly>
            <LoadingOverlay v-model="isDataLoading" />
          </ClientOnly>

          <template v-if="entityArray.length">
            <div class="relative grid grid-cols-1 min-[378px]:grid-cols-2 min-[1325px]:grid-cols-3 xl:gap-y-8 gap-4 w-full p-2">
              <EntityCard 
                v-for="entity in entityArray"
                :key="`${entity.entity_type}_${entity.id}`"
                :entity="entity"
              />
            </div>
          </template>
          <ClientOnly>
            <template v-if="!entityArray.length && !isDataLoading">
              <div class="w-full text-center">
                {{ $t('no_results') }}
              </div>
            </template>
          </ClientOnly>
        </div>

        <ClientOnly>
          <Pagination
            v-if="entityTotal > 15"
            :currentPage="filters.page"
            :totalItems="entityTotal"
            :perPage="15"
            :disabled="isDataLoading"
            @pageChange="(value) => onSingleFilterValueChange('page', parseInt(value))"
          />
        </ClientOnly>
      </div>
    </div>

    <ModalSearchLawyers
      ref="mobileSearchModalRef"
      v-model="filters.query"
      :entities="entityArray"
      :isLoading="isLawyersLoading"
      @input="onSearchInput"
    />
  </section>
</template>
